import { useMemo } from "react";
import { useAmpEmailImageKeysQuery } from "src/graphql";
import { CONTENT_NODE_URL_PATTERN } from "src/pages/lifecycle-studio/components/image-url-overrides";
import { getInlineBackgroundImageUrl } from "src/pages/lifecycle-studio/emails/email-editor/utils";
import { useRegionConfig } from "src/utils/use-region-config";

/** Email images publish at full size; a canvas tile only needs enough for a full-width image at top zoom on a 2x screen. */
export const EMAIL_TILE_IMAGE_WIDTH = 1200;

export type ResizedEmailHtml =
  | { isLoading: true }
  | { isLoading: false; html: string };

/** The published content-node images in an email: `<img src>` and inline `background-image`, which emails use for hero images. */
export function findContentNodeImageUrls(html: string): string[] {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const urls = new Set<string>();
  for (const img of Array.from(doc.querySelectorAll("img[src]"))) {
    const src = img.getAttribute("src");
    if (src) urls.add(src);
  }
  for (const el of Array.from(doc.querySelectorAll('[style*="background"]'))) {
    const url = getInlineBackgroundImageUrl(el);
    if (url) urls.add(url);
  }
  return [...urls].filter((url) => CONTENT_NODE_URL_PATTERN.test(url));
}

/** `html` with each image in `keys` pointed at its private original through the resizer, at most `width` px wide. */
export function withResizedEmailImages({
  html,
  keys,
  apiEndpoint,
  width,
}: {
  html: string;
  keys: Map<string, string>;
  apiEndpoint: string;
  width: number;
}): string {
  let resized = html;
  for (const [url, key] of keys) {
    const params = new URLSearchParams({
      key,
      width: String(width),
      format: "webp",
    });
    resized = resized.replaceAll(
      url,
      `${apiEndpoint}/agents/files?${params.toString()}`,
    );
  }
  return resized;
}

/**
 * The email's HTML with its published images swapped for resized copies of their originals, once their keys load.
 * Full-size photos make the canvas decode tens of megapixels per image on every pan and zoom.
 */
export function useResizedEmailHtml(
  html: string,
  width: number,
): ResizedEmailHtml {
  const { apiEndpoint } = useRegionConfig();
  const urls = useMemo(() => findContentNodeImageUrls(html), [html]);
  const { data, isError } = useAmpEmailImageKeysQuery(
    { urls },
    {
      enabled: urls.length > 0,
      // A content node's storage key never changes.
      staleTime: Infinity,
    },
  );
  return useMemo(() => {
    if (urls.length === 0 || isError) return { isLoading: false, html };
    if (!data) return { isLoading: true };
    return {
      isLoading: false,
      html: withResizedEmailImages({
        html,
        keys: new Map(
          data.ampEmailImageKeys.images.map(({ url, key }) => [url, key]),
        ),
        apiEndpoint,
        width,
      }),
    };
  }, [html, urls, data, isError, apiEndpoint, width]);
}
